import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ArrowDown, Menu, X, ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
  { href: "#inicio", label: "Inicio" },
  { href: "#filosofia", label: "Filosofía" },
  { href: "#proyectos", label: "Proyectos" },
  { href: "#servicios", label: "Servicios" },
  { href: "#contacto", label: "Contacto" }];


  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? "bg-white/95 backdrop-blur-sm shadow-sm" : "bg-white/80 backdrop-blur-sm"}`
      }>

      <div className="container-custom py-6 flex items-center justify-between">
        <a
          href="#inicio"
          onClick={(e) => scrollToSection(e, "#inicio")}
          data-testid="logo">

          <img
            src="https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/wvw74pkc_LOGO%20EDITABLE%20%282%29.png"
            alt="AG Studio Arquitectos"
            className="h-12 md:h-14 w-auto" />

        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) =>
          <a
            key={link.href}
            href={link.href}
            onClick={(e) => scrollToSection(e, link.href)}
            className="nav-link text-sm uppercase tracking-widest text-zinc-600 hover:text-black"
            data-testid={`nav-${link.label.toLowerCase()}`}>

              {link.label}
            </a>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-zinc-900"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu">

          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen &&
      <div
        className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-zinc-100 animate-fade-in"
        data-testid="mobile-menu">

          <div className="container-custom py-8 flex flex-col gap-6">
            {navLinks.map((link) =>
          <a
            key={link.href}
            href={link.href}
            onClick={(e) => scrollToSection(e, link.href)}
            className="text-2xl font-semibold tracking-tight text-zinc-900"
            data-testid={`mobile-nav-${link.label.toLowerCase()}`}>

                {link.label}
              </a>
          )}
          </div>
        </div>
      }
    </nav>);

};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="inicio"
      className="min-h-screen relative overflow-hidden"
      data-testid="hero-section">

      {/* Full Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1763121379543-7b7b3cae9083?w=1920&q=85"
          alt="Edificio contemporáneo de vidrio"
          className="w-full h-full object-cover"
          data-testid="hero-image" />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
      </div>

      {/* Content Overlay */}
      <div className="relative z-10 min-h-screen flex flex-col justify-end pb-24 md:pb-32">
        <div className="container-custom">
          <div className="max-w-2xl">
            <h1
              className="text-5xl md:text-6xl lg:text-7xl text-white mb-6 animate-fade-in-up opacity-0"
              style={{
                fontFamily: "Syne, sans-serif",
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                animationDelay: "0.2s",
                animationFillMode: "forwards"
              }}
              data-testid="hero-title">

              STUDIO
              <br />
              <span className="font-light">ARQUITECTOS</span>
            </h1>
            <p
              className="text-white/80 text-base md:text-lg max-w-md mb-10 animate-fade-in-up opacity-0"
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 300,
                letterSpacing: "0.02em",
                animationDelay: "0.4s",
                animationFillMode: "forwards"
              }}>Vanguardia en cada trazo, creando
espacio, luz y vida.

            </p>
            <div
              className="animate-fade-in-up opacity-0"
              style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}>

              <a
                href="#proyectos"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#proyectos")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center gap-3 text-white text-xs uppercase tracking-[0.25em] border border-white/50 px-8 py-4 hover:bg-white hover:text-black transition-all duration-500"
                data-testid="hero-cta-projects">

                Ver Proyectos
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-indicator hidden lg:block z-10"
        data-testid="scroll-indicator">

        <ArrowDown size={20} className="text-white/60" />
      </div>
    </section>);

};

// Philosophy Section
const PhilosophySection = () => {
  return (
    <section
      id="filosofia"
      className="section-padding bg-zinc-50"
      data-testid="philosophy-section">

      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative h-[500px] lg:h-[700px]">
            <img
              src="https://images.unsplash.com/photo-1766977015752-172b54ddfb3f?w=1000&q=80"
              alt="Detalle arquitectónico"
              className="w-full h-full object-cover"
              data-testid="philosophy-image" />

          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-6">
              Nuestra Filosofía
            </p>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-8"
              style={{ fontFamily: "Syne, sans-serif" }}
              data-testid="philosophy-title">Transformar Espacios


            </h2>
            <p className="text-xl text-zinc-600 mb-8 leading-relaxed">Dar soluciones, transformar y crear espacios que
satisfagan las necesidades requeridas, siempre
buscando la calidad de vida de nuestros clientes
            </p>
            <p className="text-lg text-zinc-500 mb-10 leading-relaxed">
              Nuestro enfoque combina la funcionalidad con la estética, creando
              espacios que respiran tranquilidad y armonía. La luz, el espacio y los
              materiales se convierten en los protagonistas de cada diseño.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <div>
                <p
                  className="text-4xl font-bold mb-2"
                  style={{ fontFamily: "Syne, sans-serif" }}
                  data-testid="stat-years">

                  15+
                </p>
                <p className="text-sm text-zinc-500 uppercase tracking-wider">
                  Años de experiencia
                </p>
              </div>
              <div>
                <p
                  className="text-4xl font-bold mb-2"
                  style={{ fontFamily: "Syne, sans-serif" }}
                  data-testid="stat-projects">320+


                </p>
                <p className="text-sm text-zinc-500 uppercase tracking-wider">
                  Proyectos completados
                </p>
              </div>
              <div>
                <p
                  className="text-4xl font-bold mb-2"
                  style={{ fontFamily: "Syne, sans-serif" }}
                  data-testid="stat-surface">32,175 M² +
                </p>
                <p className="text-sm text-zinc-500 uppercase tracking-wider">Superficie diseñada

                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Projects Section
const ProjectsSection = () => {
  const projects = [
  {
    id: 1,
    title: "BBVA",
    category: "Retail-Multisitios",
    year: "",
    image: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/edw5gyog_IMG_20251220_092334178_HDR.jpg",
    span: "col-span-12 md:col-span-7",
    height: "h-[400px] md:h-[500px]"
  },
  {
    id: 2,
    title: "Residencial",
    category: "Interior",
    year: "",
    image: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/zks228cx_render%20%281%29.jpeg",
    span: "col-span-12 md:col-span-5",
    height: "h-[400px] md:h-[500px]"
  },
  {
    id: 3,
    title: "Cuidado con el Perro",
    category: "Comercial",
    year: "",
    image: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/191ry9qh_WhatsApp%20Image%202026-02-06%20at%205.33.52%20PM.jpeg",
    span: "col-span-12 md:col-span-5",
    height: "h-[400px] md:h-[500px]"
  },
  {
    id: 4,
    title: "Oficinas",
    category: "Interior",
    year: "",
    image: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/0k3tewud_ALTEAP-7.jpg",
    span: "col-span-12 md:col-span-7",
    height: "h-[400px] md:h-[500px]"
  }];


  return (
    <section id="proyectos" className="section-padding" data-testid="projects-section">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">
              Portfolio
            </p>
            <h2
              className="text-4xl md:text-5xl font-bold tracking-tight"
              style={{ fontFamily: "Syne, sans-serif" }}
              data-testid="projects-title">

              Proyectos Destacados
            </h2>
          </div>
          <p className="text-zinc-500 mt-4 md:mt-0 max-w-md">


          </p>
        </div>

        <div className="projects-grid">
          {projects.map((project) =>
          <div
            key={project.id}
            className={`project-card ${project.span} ${project.height} cursor-pointer`}
            data-testid={`project-card-${project.id}`}>

              <img
              src={project.image}
              alt={project.title}
              className="w-full h-full object-cover object-center"
              style={{ objectPosition: project.id === 1 ? 'center 40%' : 'center center' }} />

              <div className="overlay"></div>
              <div className="content">
                <p className="text-xs uppercase tracking-widest text-white/70 mb-2">
                  {project.category}{project.year && ` · ${project.year}`}
                </p>
                <h3
                className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"
                style={{ fontFamily: "Syne, sans-serif" }}>

                  {project.title}
                  <ArrowUpRight size={24} />
                </h3>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Services Section
const ServicesSection = () => {
  const services = [
  {
    id: 1,
    title: "Diseño Arquitectónico",
    description:
    "Creación de espacios únicos que reflejan la identidad y necesidades de cada cliente, con un enfoque en la funcionalidad y la estética."
  },
  {
    id: 2,
    title: "Proyecto Ejecutivo",
    description:
    "Desarrollo completo de planos de diseño y especialidades necesarios para la ejecución precisa de cada obra arquitectónica."
  },
  {
    id: 3,
    title: "Diseño de Interiores",
    description:
    "Transformación de espacios interiores con un enfoque en la luz natural, materiales nobles y la creación de ambientes serenos."
  },
  {
    id: 4,
    title: "Consultoría",
    description:
    "Asesoramiento profesional en proyectos de diseño, construcción, renovación y optimización de espacios arquitectónicos."
  },
  {
    id: 5,
    title: "Gestión de Proyectos",
    description:
    "Supervisión integral del proyecto desde la conceptualización hasta la entrega final, garantizando calidad y cumplimiento de plazos."
  }];


  return (
    <section
      id="servicios"
      className="section-padding bg-zinc-50"
      data-testid="services-section">

      <div className="container-custom">
        <div className="mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">
            Lo que hacemos
          </p>
          <h2
            className="text-4xl md:text-5xl font-bold tracking-tight"
            style={{ fontFamily: "Syne, sans-serif" }}
            data-testid="services-title">

            Nuestros Servicios
          </h2>
        </div>

        <div className="divide-y divide-zinc-200">
          {services.map((service, index) =>
          <div
            key={service.id}
            className="service-item py-10 md:py-12 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start"
            data-testid={`service-item-${service.id}`}>

              <div className="md:col-span-1">
                <span className="text-xs text-zinc-400">0{index + 1}</span>
              </div>
              <div className="md:col-span-4">
                <h3
                className="text-2xl font-semibold tracking-tight"
                style={{ fontFamily: "Syne, sans-serif" }}>

                  {service.title}
                </h3>
              </div>
              <div className="md:col-span-7">
                <p className="text-zinc-500 leading-relaxed">{service.description}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Clients Section
const ClientsSection = () => {
  const clients = [
  { name: "BBVA", logo: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/f51acfpo_BBVA.png" },
  { name: "Banco Azteca", logo: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/6j22fryw_BAZT.jpg" },
  { name: "Citibanamex", logo: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/ix5hwe2x_citibanamex.jpg" },
  { name: "Compartamos Banco", logo: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/ka3fco6l_compartamos%20BANCO.jpg" },
  { name: "Cuidado con el Perro", logo: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/tht5p6ce_CP.png" },
  { name: "Elektra", logo: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/39w44466_EKT.jpg" },
  { name: "H&M", logo: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/84duh7gb_HM.jpg" },
  { name: "HSBC", logo: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/e946ykpd_HSBC.jpg" },
  { name: "Pastes Kiko's", logo: "https://customer-assets.emergentagent.com/job_minimal-architects/artifacts/fknod1xu_KIKOS.jpg" }];


  return (
    <section className="py-20 border-y border-zinc-100 bg-white" data-testid="clients-section">
      <div className="container-custom">
        <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-12 text-center">
          Clientes que confían en nosotros
        </p>
      </div>
      <div className="marquee-container overflow-hidden">
        <div className="marquee-content flex items-center">
          {[...clients, ...clients].map((client, index) =>
          <div key={index} className="mx-8 md:mx-12 flex-shrink-0">
              <img
              src={client.logo}
              alt={client.name}
              className="h-12 md:h-16 w-auto object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />

            </div>
          )}
        </div>
      </div>
    </section>);

};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    mensaje: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("¡Mensaje enviado!", {
          description: "Nos pondremos en contacto pronto."
        });
        setFormData({ nombre: "", email: "", mensaje: "" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Error al enviar", {
        description: "Por favor, inténtalo de nuevo."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="section-padding" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">
              Contacto
            </p>
            <h2
              className="text-4xl md:text-5xl font-bold tracking-tight mb-8"
              style={{ fontFamily: "Syne, sans-serif" }}
              data-testid="contact-title">

              Hablemos de tu proyecto
            </h2>
            <p className="text-lg text-zinc-500 mb-12 max-w-md">
              Estamos listos para convertir tu visión en realidad. Contáctanos y
              comencemos a crear juntos.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                  <Mail size={20} className="text-zinc-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-400 mb-1">
                    Email
                  </p>
                  <p className="text-zinc-900">contacto@studioarquitectos.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                  <Phone size={20} className="text-zinc-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-400 mb-1">
                    Teléfono
                  </p>
                  <p className="text-zinc-900">55 211 916 08</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                  <MapPin size={20} className="text-zinc-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-400 mb-1">
                    Ubicación
                  </p>
                  <p className="text-zinc-900">México</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  placeholder="Nombre"
                  required
                  className="input-custom"
                  data-testid="contact-input-nombre" />

              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  required
                  className="input-custom"
                  data-testid="contact-input-email" />

              </div>
              <div>
                <textarea
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  placeholder="Cuéntanos sobre tu proyecto..."
                  required
                  rows={5}
                  className="input-custom resize-none"
                  data-testid="contact-input-mensaje" />

              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full md:w-auto"
                data-testid="contact-submit-btn">

                {isSubmitting ? "Enviando..." : "Enviar Mensaje"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>);

};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-zinc-900 text-white py-16" data-testid="footer">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-2">
            <h3
              className="text-2xl font-bold tracking-tight mb-4"
              style={{ fontFamily: "Syne, sans-serif" }}>

              STUDIO<span className="font-light">ARQUITECTOS</span>
            </h3>
            <p className="text-zinc-400 max-w-md">Arquitectura  contemporánea. Creamos espacios que inspiran y perduran.


            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-4">
              Navegación
            </p>
            <div className="space-y-2">
              <a href="#inicio" className="block text-zinc-400 hover:text-white transition-colors">
                Inicio
              </a>
              <a href="#filosofia" className="block text-zinc-400 hover:text-white transition-colors">
                Filosofía
              </a>
              <a href="#proyectos" className="block text-zinc-400 hover:text-white transition-colors">
                Proyectos
              </a>
              <a href="#servicios" className="block text-zinc-400 hover:text-white transition-colors">
                Servicios
              </a>
              <a href="#contacto" className="block text-zinc-400 hover:text-white transition-colors">
                Contacto
              </a>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-4">
              Redes Sociales
            </p>
            <div className="space-y-2">
              <a href="#" className="block text-zinc-400 hover:text-white transition-colors">
                Instagram
              </a>
              <a href="#" className="block text-zinc-400 hover:text-white transition-colors">
                LinkedIn
              </a>
              <a href="#" className="block text-zinc-400 hover:text-white transition-colors">
                Pinterest
              </a>
            </div>
          </div>
        </div>
        <div className="pt-8 border-t border-zinc-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-500 text-sm">
            © {currentYear} Studio Arquitectos. Todos los derechos reservados.
          </p>
          <p className="text-zinc-500 text-sm">
            Diseñado con pasión por la arquitectura
          </p>
        </div>
      </div>
    </footer>);

};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <Navigation />
      <main>
        <HeroSection />
        <PhilosophySection />
        <ProjectsSection />
        <ServicesSection />
        <ClientsSection />
        <ContactSection />
      </main>
      <Footer />
    </div>);

}

export default App;